import { useCallback, useEffect, useMemo, useState } from 'react'
import { AdminPartneredHotspotDto } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import Table from '../../components/Table'
import PartneredHotspotModal from '../../components/modals/PartneredHotspotModal'
import PartneredHotspotDetailModal from '../../components/modals/PartneredHotspotDetailModal'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import { formatTicketPrice } from '../../utils/ticketPrice'

const getStatusLabel = (hotspot: AdminPartneredHotspotDto, now: Date): string => {
  if (new Date(hotspot.endsAt) <= now) return 'Ended'
  if (new Date(hotspot.startsAt) > now) return 'Upcoming'
  return 'Active'
}

const PartneredHotspotsPage = () => {
  const [hotspots, setHotspots] = useState<AdminPartneredHotspotDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [viewingHotspot, setViewingHotspot] = useState<AdminPartneredHotspotDto | null>(null)
  const [editingHotspot, setEditingHotspot] = useState<AdminPartneredHotspotDto | null>(null)
  const [deletingHotspot, setDeletingHotspot] = useState<AdminPartneredHotspotDto | null>(null)

  const loadHotspots = useCallback(async () => {
    try {
      setHotspots(await adminService.getPartneredHotspots())
    } catch {
      setError('Failed to load partnered hotspots.')
    }
  }, [])

  useEffect(() => {
    const run = async () => {
      setLoading(true)
      await loadHotspots()
      setLoading(false)
    }
    run()
  }, [loadHotspots])

  const rows = useMemo(() => {
    const now = new Date()
    return hotspots.map(hotspot => ({
      ...hotspot,
      status: getStatusLabel(hotspot, now),
      // Read by the 'eventName' cell for the ticketed mark.
      isTicketed: hotspot.eventIsTicketed,
      ticketPriceLabel: formatTicketPrice(hotspot.eventTicketPriceCents),
      rsvpsLabel: `${hotspot.stats.hotspotRsvps.toLocaleString()} of ${hotspot.stats.totalRsvps.toLocaleString()}`,
    }))
  }, [hotspots])

  const handleSaved = (saved: AdminPartneredHotspotDto) => {
    setHotspots(prev =>
      [...prev.filter(item => item.publicId !== saved.publicId), saved].sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime())
    )
  }

  const handleDelete = async () => {
    if (!deletingHotspot) return
    try {
      await adminService.deletePartneredHotspot(deletingHotspot.publicId)
      setHotspots(prev => prev.filter(item => item.publicId !== deletingHotspot.publicId))
      setEditingHotspot(null)
      setViewingHotspot(null)
    } catch {
      setError(`Failed to delete partnered hotspot "${deletingHotspot.event.name}".`)
    } finally {
      setDeletingHotspot(null)
    }
  }

  if (loading) {
    return <></>
  }

  return (
    <div className="roles-container">
      <div className="roles-header">
        <h2>Partnered hotspots</h2>
      </div>
      <p className="roles-page-intro">
        The partnered hotspot is the one event promoted in the app during its window. Windows cannot overlap, so only one hotspot is active at a time. New
        hotspots default to the selected event&apos;s dates. Open a hotspot for its funnel (modal → CTA → RSVP / tickets) and user journeys.
      </p>

      {error && <div className="error-message">{error}</div>}

      <div className="roles-actions">
        <button type="button" onClick={() => setIsCreateModalOpen(true)} className="styled-button sm">
          <span>Add partnered hotspot</span>
        </button>
      </div>

      <div className="roles-table-container">
        <div className="table-wrapper">
          <Table
            id="partnered-hotspots"
            name="Partnered hotspots"
            keyField="publicId"
            initialSortColumn="startsAt"
            initialSortOrder="desc"
            columns={[
              { label: 'Status', accessor: 'status', type: 'text' },
              { label: 'Event', accessor: 'event.name', type: 'eventName' },
              { label: 'Venue', accessor: 'venue.name', type: 'text' },
              { label: 'Partnered from', accessor: 'startsAt', type: 'datetime' },
              { label: 'Partnered until', accessor: 'endsAt', type: 'datetime' },
              { label: 'RSVPs from hotspot', accessor: 'rsvpsLabel', type: 'text' },
            ]}
            sourceData={rows}
            isPropertyBarVisible={false}
            filterTerms=""
            onRowClick={(row: AdminPartneredHotspotDto) => setViewingHotspot(row)}
            disableGlobalSearch={true}
          />
        </div>
      </div>

      <PartneredHotspotDetailModal
        isOpen={viewingHotspot !== null && editingHotspot === null && deletingHotspot === null}
        onClose={() => setViewingHotspot(null)}
        hotspot={viewingHotspot}
        status={viewingHotspot ? getStatusLabel(viewingHotspot, new Date()) : ''}
        onEdit={hotspot => {
          setViewingHotspot(null)
          setEditingHotspot(hotspot)
        }}
        onDelete={setDeletingHotspot}
      />

      <PartneredHotspotModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} hotspot={null} onSaved={handleSaved} />

      <PartneredHotspotModal
        isOpen={editingHotspot !== null && deletingHotspot === null}
        onClose={() => setEditingHotspot(null)}
        hotspot={editingHotspot}
        onDelete={setDeletingHotspot}
        onSaved={saved => {
          handleSaved(saved)
          setEditingHotspot(null)
        }}
      />

      <ConfirmationModal isOpen={deletingHotspot !== null} onClose={() => setDeletingHotspot(null)} onConfirm={handleDelete} title="Delete partnered hotspot">
        {deletingHotspot ? `Stop partnering "${deletingHotspot.event.name}" and delete this hotspot?` : ''}
      </ConfirmationModal>
    </div>
  )
}

export default PartneredHotspotsPage
